// HTTP constants
const HTTP_METHODS = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  DELETE: "DELETE",
};

const CONTENT_TYPES = {
  JSON: "application/json",
  FORM_DATA: "multipart/form-data",
};

// Custom API Error
class ApiError {
  constructor(message, status = 500, details = null) {
    this.name = "ApiError";
    this.message = message;
    this.status = status;
    this.details = details;
  }
}

// Environment config
const API_URL = process.env.REACT_APP_BACKEND_URL || "https://backend.swisstaxfile.ch/api/";
//const API_URL = "https://backend.swisstaxfile.ch/api/" || "http://localhost:3000";
const REQUEST_TIMEOUT = 30000;
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

// Interceptors
const interceptors = [];

export function addInterceptor(interceptor) {
  interceptors.push(interceptor);
}

// Refresh token state
let isRefreshing = false;
let refreshPromise = null;

/**
 * Refresh access token safely
 */
async function refreshAccessToken() {
  if (isRefreshing) return refreshPromise;

  const refreshToken = localStorage.getItem("refreshToken");

  if (!refreshToken) {
    return null;
  }

  isRefreshing = true;

  refreshPromise = fetch(`${API_URL}/auth/refresh-tokens`, {
    method: HTTP_METHODS.POST,
    headers: {
      "Content-Type": CONTENT_TYPES.JSON,
    },
    body: JSON.stringify({ refreshToken }),
  })
    .then(async (res) => {
      if (!res.ok) return null;

      const data = await res.json();

      const newAccess = data?.access?.token;
      const newRefresh = data?.refresh?.token;

      if (newAccess) {
        localStorage.setItem("accessToken", newAccess);
      }

      if (newRefresh) {
        localStorage.setItem("refreshToken", newRefresh);
      }

      return newAccess;
    })
    .catch(() => {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");

      window.location.href = "/de/login";
      return null;
    })
    .finally(() => {
      isRefreshing = false;
    });

  return refreshPromise;
}

/**
 * Core request function
 */
async function request(endpoint, method, body, isFormData = false, signal, retries = 0) {
  try {
    const token = localStorage.getItem("accessToken");

    const headers = {};

    if (!isFormData) {
      headers["Content-Type"] = CONTENT_TYPES.JSON;
    }

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    let config = {
      method,
      headers,
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
      signal,
    };

    // Apply request interceptors
    interceptors.forEach((interceptor) => {
      if (interceptor.onRequest) {
        config = interceptor.onRequest(config);
      }
    });

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...config,
      signal: signal || controller.signal,
    });

    clearTimeout(timeoutId);

    let modifiedResponse = response;

    // Response interceptors
    interceptors.forEach((interceptor) => {
      if (interceptor.onResponse) {
        modifiedResponse = interceptor.onResponse(modifiedResponse);
      }
    });

    let data;
    const contentType = modifiedResponse.headers.get("content-type");

    if (modifiedResponse.status === 204) {
      data = null;
    } else if (contentType?.includes(CONTENT_TYPES.JSON)) {
      data = await modifiedResponse.json();
    } else {
      data = await modifiedResponse.text();
    }

    // Handle errors safely
    if (!modifiedResponse.ok) {
      // 401 refresh token
      if (modifiedResponse.status === 401 && retries < 1) {
        const newToken = await refreshAccessToken();

        if (newToken) {
          return request(endpoint, method, body, isFormData, signal, retries + 1);
        }
      }

      // 429 retry
      if (modifiedResponse.status === 429 && retries < MAX_RETRIES) {
        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAY * Math.pow(2, retries))
        );

        return request(endpoint, method, body, isFormData, signal, retries + 1);
      }

      const errorMessage =
        data?.message ||
        modifiedResponse.statusText ||
        `HTTP error ${modifiedResponse.status}`;

      return {
        success: false,
        error: new ApiError(errorMessage, modifiedResponse.status, data),
      };
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    if (error.name === "AbortError") {
      return {
        success: false,
        error: new ApiError("Request timed out", 408),
      };
    }

    const modifiedError = new ApiError(error.message || "Unknown error");

    interceptors.forEach((interceptor) => {
      if (interceptor.onError) {
        interceptor.onError(modifiedError);
      }
    });

    console.error(`API Error (${method} ${API_URL}${endpoint}):`, modifiedError);

    return {
      success: false,
      error: modifiedError,
    };
  }
}

/**
 * API client methods
 */
export const apiClient = {
  get: (endpoint, signal) =>
    request(endpoint, HTTP_METHODS.GET, undefined, false, signal),

  post: (endpoint, body, isFormData = false, signal) =>
    request(endpoint, HTTP_METHODS.POST, body, isFormData, signal),

  put: (endpoint, body, isFormData = false, signal) =>
    request(endpoint, HTTP_METHODS.PUT, body, isFormData, signal),

  delete: (endpoint, signal) =>
    request(endpoint, HTTP_METHODS.DELETE, undefined, false, signal),

  postFormData: (endpoint, formData, signal) =>
    request(endpoint, HTTP_METHODS.POST, formData, true, signal),

  putFormData: (endpoint, formData, signal) =>
    request(endpoint, HTTP_METHODS.PUT, formData, true, signal),
};
